// ============================================
// File: components/pages/Support.jsx
// Support page displayed when runtime errors occur
// ============================================

import React from 'react';
import { AlertCircle, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../../styles/support.css';

const Support = ({ 
  error = null, 
  errorInfo = null,
  resetError = null,
  showBackButton = true 
}) => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (resetError) {
      resetError();
    }
    navigate(-1);
  };

  const handleGoHome = () => {
    if (resetError) {
      resetError();
    }
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Extract error message if available
  const errorMessage = error?.message || error?.error || 'An unexpected error occurred';
  const isSignupError = errorMessage.toLowerCase().includes('signup') || 
                        errorMessage.toLowerCase().includes('auth0') ||
                        errorMessage.toLowerCase().includes('registration');

  return (
    <div className="support-page">
      <div className="support-container">
        {/* Error Icon */}
        <div className="support-icon-wrapper">
          <AlertCircle className="support-icon" size={64} />
        </div>

        {/* Error Title */}
        <h1 className="support-title">
          {isSignupError ? 'Signup Error' : 'Something Went Wrong'}
        </h1>

        {/* Error Description */}
        <div className="support-description">
          <p>
            We encountered an issue while processing your request. 
            {isSignupError && ' Your account registration could not be completed at this time.'}
          </p>
          <p>
            Our team has been notified and we're working to resolve this issue.
          </p>
        </div>

        {/* Error Details (optional, for development) */}
        {process.env.NODE_ENV === 'development' && error && (
          <div className="support-error-details">
            <h3>Error Details (Development Only)</h3>
            <div className="error-message">
              <strong>Message:</strong> {errorMessage}
            </div>
            {errorInfo && (
              <div className="error-stack">
                <strong>Stack Trace:</strong>
                <pre>{errorInfo.componentStack}</pre>
              </div>
            )}
          </div>
        )}

        {/* Contact Information */}
        <div className="support-contact">
          <div className="support-contact-header">
            <Mail size={20} />
            <h3>Need Immediate Help?</h3>
          </div>
          <p>Please contact our support team for assistance:</p>
          <a 
            href="mailto:nbipinraj@bipzapp.com?subject=Signup Error - Support Request"
            className="support-email"
          >
            nbipinraj@bipzapp.com
          </a>
          <p className="support-contact-note">
            Please include details about what you were trying to do when this error occurred.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="support-actions">
          <button 
            onClick={handleRefresh} 
            className="btn-support btn-primary"
          >
            <RefreshCw size={18} />
            Try Again
          </button>
          
          {showBackButton && (
            <button 
              onClick={handleGoBack} 
              className="btn-support btn-secondary"
            >
              <ArrowLeft size={18} />
              Go Back
            </button>
          )}
          
          <button 
            onClick={handleGoHome} 
            className="btn-support btn-outline"
          >
            Go to Home
          </button>
        </div>

        {/* Additional Help */}
        <div className="support-help-text">
          <p>
            <strong>What to do next:</strong>
          </p>
          <ul>
            <li>Check your internet connection</li>
            <li>Try refreshing the page</li>
            <li>Clear your browser cache and cookies</li>
            <li>Try again in a few minutes</li>
            <li>If the problem persists, contact support using the email above</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Support;